import { classNames } from '@fullstory/util/fmt';
import React, { forwardRef, useMemo } from 'react';
import { identifyComponent } from '../../util';
import { ListContext, ListContextProps } from './context';
import css from './index.module.scss';

export interface ListProps extends ListContextProps {
  testId?: string;

  /**
   * The content of the component.
   */
  children: React.ReactNode;

  /**
   * An optional class name to apply to the component.
   */
  className?: string;

  /**
   * If `true`, vertical padding is removed from the list.
   */
  disablePadding?: boolean;

  /**
   * A string that will be passed on to the native html role attribute.
   */
  role?: string;
}

/**
 * An unordered list of items.
 */
export const List = forwardRef<HTMLUListElement, ListProps>(
  (
    {
      testId = 'list',
      children,
      className,
      disablePadding = false,
      disableGutters = false,
      compact = false,
      role,
      ...rest
    },
    ref,
  ) => {
    const listContextProps = useMemo(
      () => ({
        compact,
        disableGutters,
      }),
      [compact, disableGutters],
    );
    return (
      <ListContext.Provider value={listContextProps}>
        <ul
          data-component="List"
          ref={ref}
          data-testid={testId}
          className={classNames(css.listComponent, className, {
            [css.disablePadding]: disablePadding,
          })}
          role={role}
          {...rest}
          {...identifyComponent('list', rest)}
        >
          {children}
        </ul>
      </ListContext.Provider>
    );
  },
);

List.displayName = 'List';
